import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, CircularProgress, Checkbox, FormControlLabel,
    Dialog, DialogTitle, DialogActions, Button, Autocomplete, TextField, IconButton, Paper, TablePagination, Select, MenuItem, FormControl
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

const WashList = () => {
  const { subscriptionId } = useParams();
  const [washes, setWashes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedWashId, setSelectedWashId] = useState(null);
  const [washerLoading, setWasherLoading] = useState(false);
  const [editWasherOpen, setEditWasherOpen] = useState(false);
  const [editWashId, setEditWashId] = useState(null);
  const [editWasher, setEditWasher] = useState(null);
  const [editWasherQuery, setEditWasherQuery] = useState('');
  const [editWasherOptions, setEditWasherOptions] = useState([]);
  const [editNotesId, setEditNotesId] = useState(null);
  const [editNotesValue, setEditNotesValue] = useState('');
  const [editNotesOpen, setEditNotesOpen] = useState(false);
  const [editDateTimeOpen, setEditDateTimeOpen] = useState(false);
  const [editDateTimeId, setEditDateTimeId] = useState(null);
  const [editDateTimeValue, setEditDateTimeValue] = useState('');
  const editWasherDebounceRef = React.useRef();
  const [subscription, setSubscription] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  // Separate states for pending and non-pending
  const [pendingWashes, setPendingWashes] = useState([]);
  const [nonPendingWashes, setNonPendingWashes] = useState([]);
  const [pendingPage, setPendingPage] = useState(0);
  const [pendingRowsPerPage, setPendingRowsPerPage] = useState(4);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [nonPendingPage, setNonPendingPage] = useState(0);
  const [nonPendingRowsPerPage, setNonPendingRowsPerPage] = useState(4);
  const [nonPendingTotal, setNonPendingTotal] = useState(0);

  // NEW: washType edit dialog state
  const [washTypeDialogOpen, setWashTypeDialogOpen] = useState(false);
  const [washTypeValue, setWashTypeValue] = useState('');

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    setLoading(true);
    // Fetch subscription details
    fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/users/${subscriptionId}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        console.log(result)
        setSubscription(result || null);
      })
      .catch(() => {
        setSubscription(null);
      });

    // Fetch pending washes
    let pendingUrl = `${process.env.REACT_APP_SERVER_URI}/washes/pending?subscriptionId=${subscriptionId}&page=${pendingPage + 1}&limit=${pendingRowsPerPage}&status=pending&sort=${encodeURIComponent('{"date":1}')}`;
    fetch(pendingUrl, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        setPendingWashes(result.data || []);
        setPendingTotal(result.total || 0);
        setLoading(false);
      })
      .catch(() => {
        setPendingWashes([]);
        setPendingTotal(0);
        setLoading(false);
      });

    // Fetch non-pending washes
    let nonPendingUrl = `${process.env.REACT_APP_SERVER_URI}/washes/nonPending?subscriptionId=${subscriptionId}&page=${nonPendingPage + 1}&limit=${nonPendingRowsPerPage}`;
    fetch(nonPendingUrl, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        setNonPendingWashes(result.data || []);
        setNonPendingTotal(result.total || 0);
      })
      .catch(() => {
        setNonPendingWashes([]);
        setNonPendingTotal(0);
      });
  }, [subscriptionId, pendingPage, pendingRowsPerPage, nonPendingPage, nonPendingRowsPerPage]);

  const fetchWasherOptions = (search = '') => {
    setWasherLoading(true);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/washers/options?search=${encodeURIComponent(search)}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        setEditWasherOptions(result || []);
        setWasherLoading(false);
      })
      .catch(() => {
        setEditWasherOptions([]);
        setWasherLoading(false);
      });
  };

  const handleCheckboxClick = (washId) => {
    console.log(washes, washId);
    setSelectedWashId(washId);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedWashId) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    await fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update/${selectedWashId}`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ interior: ![...pendingWashes, ...nonPendingWashes].find(v=>v._id===selectedWashId)?.interior }),
    })
      .then(res => res.json())
      .then(() => {
        if (pendingWashes.find(wash=>wash._id===selectedWashId)){
          setPendingWashes(washes =>
          washes.map(w =>
            w._id === selectedWashId ? { ...w, interior: !w.interior } : w
          ))
        }else
        {
          setNonPendingWashes(washes=>washes.map(w=>w._id === selectedWashId? {...w, interior: !w.interior}:w))
        }
        setConfirmOpen(false);
        setSelectedWashId(null);
      })
      .catch(() => {
        alert('Failed to mark as interior wash.');
        setConfirmOpen(false);
        setSelectedWashId(null);
      });
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setSelectedWashId(null);
  };

  const handleEditWasherOpen = (wash) => {
    setEditWashId(wash._id);
    setEditWasher(wash.washer || null);
    setEditWasherQuery(wash.washerName || '');
    setEditWasherOptions([{ _id: wash.washer, name: wash.washerName }]);
    setEditWasherOpen(true);
    fetchWasherOptions(wash.washerName || '');
  };

  const handleEditWasherInputChange = (event, value) => {
    setEditWasherQuery(value);
    if (editWasherDebounceRef.current) clearTimeout(editWasherDebounceRef.current);
    editWasherDebounceRef.current = setTimeout(() => {
      fetchWasherOptions(value);
    }, 400);
  };

  const handleEditWasherSave = () => {
    if (!editWashId || !editWasher) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update-washer/${editWashId}`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ washerId: editWasher._id }),
    })
      .then(res => res.json())
      .then(() => {
        setWashes(washes =>
          washes.map(w =>
            w._id === editWashId
              ? { ...w, washer: editWasher._id, washerName: editWasher.name }
              : w
          )
        );
        setEditWasherOpen(false);
        setEditWashId(null);
        setEditWasher(null);
        setEditWasherQuery('');
        setEditWasherOptions([]);
      })
      .catch(() => {
        alert('Failed to update washer.');
      });
  };

  const handleEditNotesOpen = (wash) => {
    setEditNotesId(wash._id);
    setEditNotesValue(wash.notes || '');
    setEditNotesOpen(true);
  };

  const handleEditNotesSave = () => {
    if (!editNotesId) return;
    console.log(editNotesValue, editNotesId);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update-notes/${editNotesId}`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ notes: editNotesValue }),
    })
      .then(res => res.json())
      .then(() => {
        setWashes(washes =>
          washes.map(w =>
            w._id === editNotesId ? { ...w, notes: editNotesValue } : w
          )
        );
        setEditNotesOpen(false);
        setEditNotesId(null);
        setEditNotesValue('');
      })
      .catch(() => {
        alert('Failed to update notes.');
      });
  };

  const handleEditDateTimeOpen = (wash) => {
    setEditDateTimeId(wash._id);
    if (wash.date) {
      const utcDate = new Date(wash.date);
      // Convert UTC to IST
      // const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60000));
      
      // If time is 00:00:00, set to 09:00:00 in IST
      // if (istDate.getHours() === 0 && istDate.getMinutes() === 0 && istDate.getSeconds() === 0) {
      //   istDate.setHours(9, 0, 0, 0);
      // }
      
      // Format as datetime-local (YYYY-MM-DDTHH:MM)
      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getDate()).padStart(2, '0');
      const hours = String(utcDate.getHours()).padStart(2, '0');
      const minutes = String(utcDate.getMinutes()).padStart(2, '0');
      
      setEditDateTimeValue(`${year}-${month}-${day}T${hours}:${minutes}`);
    } else {
      setEditDateTimeValue('');
    }
    setEditDateTimeOpen(true);
  };

  const handleEditDateTimeSave = () => {
    if (!editDateTimeId) return;
    console.log(editDateTimeValue);
    
    // Parse the datetime-local value and convert IST to UTC
    const [datePart, timePart] = editDateTimeValue.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    // Create IST date
    const istDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
    
    // Convert IST to UTC (subtract 5.5 hours)
    // const utcDate = new Date(istDate.getTime() - (5.5 * 60 * 60000));
    
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/washes/update-datetime/${editDateTimeId}`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ date: istDate.toISOString() }),
    })
      .then(res => res.json())
      .then((data) => {
        // Update both pending and non-pending washes
        setPendingWashes(washes =>
          washes.map(w =>
            w._id === editDateTimeId ? { ...w, date: istDate.toISOString() } : w
          )
        );
        setNonPendingWashes(washes =>
          washes.map(w =>
            w._id === editDateTimeId ? { ...w, date: istDate.toISOString() } : w
          )
        );
        setEditDateTimeOpen(false);
        setEditDateTimeId(null);
        setEditDateTimeValue('');
      })
      .catch(() => {
        alert('Failed to update date & time.');
      });
  };

  const formatDate = (dateStr) => {
    console.log(dateStr);
    if (!dateStr) return '-';
    // Convert UTC to IST for display
    const utcDate = new Date(dateStr);
    const day = String(utcDate.getDate()).padStart(2, '0');
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const year = utcDate.getFullYear();
    const time = utcDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${day}-${month}-${year} ${time}`;
  };

  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return '-';
    // Convert UTC to IST for display
    const utcDate = new Date(dateStr);
    // const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60000));
    return utcDate.toLocaleDateString('en-US', { weekday: 'long' });
  };

  // const handleChangePage = (event, newPage) => {
  //   setPage(newPage);
  // };

  // const handleChangeRowsPerPage = (event) => {
  //   setRowsPerPage(parseInt(event.target.value, 12));
  //   setPage(0);
  // };

  const handlePendingChangePage = (event, newPage) => {
    setPendingPage(newPage);
  };

  const handlePendingChangeRowsPerPage = (event) => {
    setPendingRowsPerPage(parseInt(event.target.value, 10));
    setPendingPage(0);
  };

  const handleNonPendingChangePage = (event, newPage) => {
    setNonPendingPage(newPage);
  };

  const handleNonPendingChangeRowsPerPage = (event) => {
    setNonPendingRowsPerPage(parseInt(event.target.value, 10));
    setNonPendingPage(0);
  };
console.log(pendingWashes.map(v=>v._id));

  // replace the old prompt-based handler with an opener
  const handleOpenWashTypeDialog = () => {
    setWashTypeValue(subscription?.washType || '');
    setWashTypeDialogOpen(true);
  };

  const handleSaveWashType = async () => {
    if (!subscription) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptions/update-washtype/${subscriptionId}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ washType: washTypeValue }),
      });
      if (!res.ok) throw new Error('Failed to update wash type');
      setSubscription(prev => prev ? { ...prev, washType: washTypeValue } : prev);
      setWashTypeDialogOpen(false);
    } catch (err) {
      console.error(err);
      alert('Failed to update wash type.');
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Washes for Subscription</Typography>
        {subscription && (
          <Box sx={{ mb: 2, p: 2, background: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Customer: {subscription.userId?.name || subscription.customerName || '-'}
            </Typography>
            <Typography variant="body2">
              Phone: {subscription.userId?.phone || '-'}
            </Typography>
            <Typography variant="body2">
              Plan: {subscription.currentSubscriptionPlan || '-'}
            </Typography>
            <Typography variant="body2">
              Start Date: {subscription.startDate ? formatDate(subscription.startDate) : '-'}
            </Typography>
            <Typography variant="body2">
              Expire Date: {subscription.expireDate ? formatDate(subscription.expireDate) : '-'}
            </Typography>
            <Typography variant="body2">
              Status: {subscription.status || '-'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">WashType: {subscription.currentWashType || '-'}</Typography>
              <IconButton size="small" onClick={handleOpenWashTypeDialog} aria-label="Edit wash type">
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            {/* Car Details */}
            <Box sx={{ mt: 2, p: 1, background: '#e3e3e3', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>Car Details</Typography>
          <Typography variant="body2">
            Model: {subscription.carId?.model || '-'}
          </Typography>
          <Typography variant="body2">
            Reg No: {subscription.carId?.regNo || '-'}
          </Typography>
            </Box>
          </Box>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : (
          <>
            {/* Pending Washes */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Pending Washes</Typography>
            <Grid container spacing={3} justifyContent="flex-start">
              {pendingWashes?.map((wash, idx) => (
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3} key={wash._id || idx} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', minWidth: 320, maxWidth: 340, margin: '0 auto'}}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>
                Date & Time: {wash.date ? formatDate(wash.date) : '-'}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleEditDateTimeOpen(wash)}
                sx={{ ml: 1 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Day: {wash.date ? getDayOfWeek(wash.date) : '-'}
            </Typography>
            <Typography>Status: {wash.status || '-'}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>Washer: {wash.washerName || '-'}</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleEditWasherOpen(wash)}
                sx={{ ml: 1 }}
              >
                Change
              </Button>
            </Box>
            <Typography>Car: {wash.carRegNo || '-'}</Typography>
            <Typography sx={{ mt: 1 }} color="text.secondary">
              Notes: {wash.notes || '-'}
              <Button
                size="small"
                variant="text"
                sx={{ ml: 1 }}
                onClick={() => handleEditNotesOpen(wash)}
              >
                Edit
              </Button>
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
              checked={!!wash?.interior}
              onChange={() => handleCheckboxClick(wash._id)}
                />
              }
              label="Mark as Interior Wash"
            />
            {/* Add more wash details as needed */}
                </CardContent>
              </Card>
            </Grid>
          ))}
          {pendingWashes.length === 0 && !loading && (
            <Grid item xs={12}>
              <Typography align="center" color="text.secondary">No pending washes found for this subscription.</Typography>
            </Grid>
          )}
        </Grid>
        <Box mt={2}>
          <Paper>
            <Box>
              <TablePagination
                component="div"
                count={pendingTotal}
                page={pendingPage}
                onPageChange={handlePendingChangePage}
                rowsPerPage={pendingRowsPerPage}
                onRowsPerPageChange={handlePendingChangeRowsPerPage}
                rowsPerPageOptions={[4, 8, 12, 20]}
              />
            </Box>
          </Paper>
        </Box>
          {/* Non-Pending Washes */}
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>Other Washes</Typography>
          <Grid container spacing={3} justifyContent="flex-start">
            {nonPendingWashes?.map((wash, idx) => (
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3} key={wash._id || idx} sx={{ display: 'flex' }}>
                <Card sx={{ width: '100%', minWidth: 320, maxWidth: 340, margin: '0 auto'}}>
                  <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {/* ...existing wash card code, use wash as data... */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>
                        Date & Time: {wash.date ? formatDate(wash.date) : '-'}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleEditDateTimeOpen(wash)}
                        sx={{ ml: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Day: {wash.date ? getDayOfWeek(wash.date) : '-'}
                    </Typography>
                    <Typography>Status: {wash.status || '-'}</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>Washer: {wash.washerName || '-'}</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEditWasherOpen(wash)}
                        sx={{ ml: 1 }}
                      >
                        Change
                      </Button>
                    </Box>
                    <Typography>Car: {wash.carRegNo || '-'}</Typography>
                    <Typography sx={{ mt: 1 }} color="text.secondary">
                      Notes: {wash.notes || '-'}
                      <Button
                        size="small"
                        variant="text"
                        sx={{ ml: 1 }}
                        onClick={() => handleEditNotesOpen(wash)}
                      >
                        Edit
                      </Button>
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!wash.interior}
                          onChange={() => handleCheckboxClick(wash._id)}
                        />
                      }
                      label="Mark as Interior Wash"
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {nonPendingWashes.length === 0 && !loading && (
              <Grid item xs={12}>
                <Typography align="center" color="text.secondary">No other washes found for this subscription.</Typography>
              </Grid>
            )}
          </Grid>
          <Box mt={2}>
            <Paper>
              <Box>
                <TablePagination
                  component="div"
                  count={nonPendingTotal}
                  page={nonPendingPage}
                  onPageChange={handleNonPendingChangePage}
                  rowsPerPage={nonPendingRowsPerPage}
                  onRowsPerPageChange={handleNonPendingChangeRowsPerPage}
                  rowsPerPageOptions={[4, 8, 12, 20]}
                />
              </Box>
            </Paper>
          </Box>
          </>
      )}
      <Dialog open={editWasherOpen} onClose={() => setEditWasherOpen(false)}>
        <DialogTitle>Change Washer</DialogTitle>
        <Box px={3} py={2}>
          <Autocomplete
            options={editWasherOptions}
            getOptionLabel={option => option.name || ''}
            loading={washerLoading}
            value={editWasher}
            onChange={(event, value) => setEditWasher(value)}
            inputValue={editWasherQuery}
            onInputChange={handleEditWasherInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Washer"
                margin="normal"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {washerLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setEditWasherOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleEditWasherSave} variant="contained" color="primary" disabled={!editWasher}>
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={confirmOpen} onClose={handleCancel}>
        <DialogTitle>Are you sure you want to {[...pendingWashes,...nonPendingWashes].find(v=>v._id===selectedWashId)?.interior ? 'unmark' : 'mark'} this as an interior wash?</DialogTitle>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editNotesOpen} onClose={() => setEditNotesOpen(false)}>
        <DialogTitle>Edit Notes</DialogTitle>
        <Box px={3} py={2}>
          <TextField
            label="Notes"
            value={editNotesValue}
            onChange={e => setEditNotesValue(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            margin="normal"
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setEditNotesOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleEditNotesSave} variant="contained" color="primary">
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={editDateTimeOpen} onClose={() => setEditDateTimeOpen(false)}>
        <DialogTitle>Edit Date & Time</DialogTitle>
        <Box px={3} py={2}>
          <TextField
            label="Date & Time"
            type="datetime-local"
            value={editDateTimeValue}
            onChange={e => setEditDateTimeValue(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setEditDateTimeOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleEditDateTimeSave} variant="contained" color="primary">
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* NEW: Dialog to edit wash type */}
      <Dialog open={washTypeDialogOpen} onClose={() => setWashTypeDialogOpen(false)}>
        <DialogTitle>Edit Wash Type</DialogTitle>
        <Box px={3} py={2} sx={{ minWidth: 360 }}>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Select
              value={washTypeValue}
              onChange={(e) => setWashTypeValue(e.target.value)}
              displayEmpty
              renderValue={(v) => (v ? v : "Select wash type (optional)")}
              MenuProps={{
                PaperProps: { sx: { bgcolor: "#23282e", color: "white", zIndex: 11000 } },
                MenuListProps: { sx: { bgcolor: "#23282e", zIndex: 11000 } },
              }}
              sx={{
                backgroundColor: "transparent",
                color: "black",
                borderRadius: 1,
                border: "1px solid rgba(0,0,0,0.12)",
                ".MuiSelect-select": { paddingTop: "10px", paddingBottom: "10px", color: "black" },
                "& .MuiSvgIcon-root": { color: "inherit" },
              }}
            >
              <MenuItem value="waterless">Waterless car wash</MenuItem>
              <MenuItem value="bucket">Water Wash (Bucket wash)</MenuItem>
              <MenuItem value="jet-pressure">Jet Pressure wash</MenuItem>
            </Select>
          </FormControl>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setWashTypeDialogOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleSaveWashType} variant="contained" color="primary">Save</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default WashList;
